import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import Pricing from "../components/Pricing";
import { Badge } from "../components/ui/badge";
import { usePageContent, renderHTML } from "../hooks/usePageContent";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Database,
  Server,
  Code2,
  Zap,
  Shield,
  BarChart3,
  ArrowRight,
  Check,
  Menu,
  X,
  MessageCircle,
  Send,
  HelpCircle,
  Clock,
  Users,
  FileDown,
  History,
  Layers,
  Terminal,
  Lock,
  Gauge,
  ChevronDown,
  Bot,
  Minimize2,
  Mail,
  Building2,
  Globe
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Database types configuration
const databaseTypes = [
  { name: "MS SQL Server", color: "blue", icon: "🗄️" },
  { name: "MySQL", color: "orange", icon: "🐬" },
  { name: "PostgreSQL", color: "sky", icon: "🐘" },
  { name: "MongoDB", color: "green", icon: "🍃" },
  { name: "Oracle", color: "red", icon: "☀️" },
  { name: "SQLite", color: "gray", icon: "📁" },
  { name: "MariaDB", color: "amber", icon: "🔷" },
  { name: "Redis", color: "rose", icon: "⚡" },
  { name: "Cassandra", color: "cyan", icon: "👁️" },
  { name: "DynamoDB", color: "yellow", icon: "🔶" },
  { name: "Couchbase", color: "pink", icon: "🛋️" },
  { name: "Firebase", color: "amber", icon: "🔥" },
];

// FAQ data is generated inside the component to use dynamic siteName

// Chatbot component
const ChatBot = ({ isOpen, onClose, primaryColor }) => {
  const [messages, setMessages] = useState([
    { role: "bot", content: "Hi! I'm QueryBot, your database assistant. How can I help you today?" }
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim()) return;
    
    const userMessage = input.trim();
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setInput("");
    setIsTyping(true);
    
    try {
      const response = await fetch(`${API_URL}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMessage, session_id: sessionId })
      });
      
      const data = await response.json();
      setSessionId(data.session_id);
      setMessages(prev => [...prev, { role: "bot", content: data.response }]);
    } catch (error) {
      console.error("Chat error:", error);
      setMessages(prev => [...prev, { role: "bot", content: "Sorry, I'm having trouble connecting. Please try again!" }]);
    } finally {
      setIsTyping(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-4 right-4 w-96 h-[500px] bg-card border border-border rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden" data-testid="chatbot-container">
      {/* Header */}
      <div className="p-4 border-b border-border flex items-center justify-between" style={{ backgroundColor: primaryColor }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">QueryBot</h3>
            <p className="text-xs text-white/70">AI Assistant</p>
          </div>
        </div>
        <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="chatbot-close-btn">
          <Minimize2 className="w-5 h-5" />
        </button>
      </div>
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chatbot-messages">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chatbot-message-${idx}`}>
            <div 
              className={`max-w-[80%] p-3 rounded-2xl text-sm whitespace-pre-line ${
                msg.role === "user" 
                  ? "bg-primary text-primary-foreground rounded-br-md" 
                  : "bg-muted rounded-bl-md"
              }`}
              style={msg.role === "user" ? { backgroundColor: primaryColor } : {}}
            >
              {msg.content}
            </div>
          </div>
        ))}
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-muted p-3 rounded-2xl rounded-bl-md">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input */}
      <div className="p-4 border-t border-border">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type your question..."
            onKeyPress={(e) => e.key === "Enter" && handleSend()}
            className="flex-1"
            data-testid="chatbot-input"
          />
          <Button size="icon" onClick={handleSend} style={{ backgroundColor: primaryColor }} data-testid="chatbot-send-btn">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [footerTime, setFooterTime] = useState(new Date());
  const { getContent } = usePageContent('landing');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/settings/public`);
        setSettings(response.data);
      } catch (error) {
        console.error("Failed to fetch settings");
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setFooterTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const features = [
    {
      icon: Database,
      title: "12 Database Types",
      description: "Connect to SQL Server, MySQL, PostgreSQL, MongoDB, Oracle, SQLite, MariaDB, Redis, Cassandra, DynamoDB, Couchbase & Firebase."
    },
    {
      icon: Code2,
      title: "Powerful Query Editor",
      description: "Monaco editor with syntax highlighting, auto-complete, and intelligent query templates for every database type."
    },
    {
      icon: BarChart3,
      title: "Real-Time Metrics Dashboard",
      description: "Monitor queries per minute, response times, rows fetched, and success rates per connection with live analytics."
    },
    {
      icon: Zap,
      title: "Lightning Fast Execution",
      description: "Execute queries with real-time results, performance metrics, and execution time tracking."
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      description: "SSL/TLS encryption, secure credential storage, and support for SSH tunneling."
    },
    {
      icon: FileDown,
      title: "One-Click Export",
      description: "Export query results to CSV or JSON format instantly. Perfect for reports and data sharing."
    },
    {
      icon: History,
      title: "Query History & Analytics",
      description: "Track all executed queries with detailed execution stats. Analyze performance over time."
    },
    {
      icon: Layers,
      title: "Saved Queries Library",
      description: "Save, organize, and share your frequently used queries with names and descriptions."
    },
    {
      icon: Terminal,
      title: "CRUD Operations",
      description: "Create, read, update, and delete data directly from the interface. In-cell editing supported."
    },
    {
      icon: Gauge,
      title: "Performance Metrics",
      description: "Track query execution time, row counts, and optimize your database operations."
    },
    {
      icon: Users,
      title: "Multi-Connection",
      description: "Manage unlimited database connections. Switch between them instantly."
    },
    {
      icon: Lock,
      title: "Role-Based Access",
      description: "Secure access controls with subscription management and admin oversight."
    }
  ];

  const pricingFeatures = [
    "Unlimited database connections",
    "All 12 database types supported",
    "Query history & saved queries",
    "CSV & JSON export",
    "Visual database explorer",
    "In-cell CRUD operations",
    "Performance metrics",
    "Priority email support",
    "Cancel anytime - no commitment"
  ];

  const siteName = settings?.site_name || "AIQueryStudio";
  const tagline = settings?.tagline || "Professional Database Management";

  const faqItems = [
    {
      question: `What databases does ${siteName} support?`,
      answer: `${siteName} supports 12 popular databases: MS SQL Server, MySQL, PostgreSQL, MongoDB, Oracle, SQLite, MariaDB, Redis, Cassandra, DynamoDB, Couchbase, and Firebase. We're constantly adding support for more databases.`
    },
    {
      question: "Is there a free trial available?",
      answer: `Yes! You can start with a free trial to explore all features. No credit card required to get started. We believe in letting you experience the full power of ${siteName} before committing.`
    },
    {
      question: "Can I cancel my subscription anytime?",
      answer: "Absolutely. There's no long-term commitment required. You can cancel your subscription at any time, and you'll continue to have access until the end of your billing period."
    },
    {
      question: "How secure are my database connections?",
      answer: "Security is our top priority. All connections are encrypted using industry-standard SSL/TLS. Your credentials are stored securely and never shared. We also support SSH tunneling for additional security."
    },
    {
      question: "Can I export my query results?",
      answer: "Yes! You can export query results to CSV or JSON format with a single click. This makes it easy to share data with team members or import into other applications."
    },
    {
      question: "Do you offer team or enterprise plans?",
      answer: "We're working on team and enterprise plans with features like shared connections, team workspaces, and audit logs. Contact us if you're interested in early access."
    },
    {
      question: "What kind of support do you offer?",
      answer: "We offer email support for all users, with priority support for Pro subscribers. Our AI chatbot is available 24/7 for instant answers to common questions."
    },
    {
      question: "Can I save and organize my queries?",
      answer: `Yes! You can save queries with names and descriptions, organize them by connection, and quickly re-run them from your saved queries library. Query history is also maintained for easy reference.`
    }
  ];
  const primaryColor = settings?.primary_color || "#22c55e";
  const logoUrl = settings?.logo_url || null;
  const price = settings?.subscription_price || 1.00;
  const paywallEnabled = settings?.paywall_enabled;
  const weeklyPriceEnabled = settings?.weekly_price_enabled !== false;
  const weeklyPrice = settings?.weekly_price || 0.35;
  const annualPriceEnabled = settings?.annual_price_enabled !== false;
  const annualDiscount = settings?.annual_discount_percent || 15;
  const annualPrice = settings?.annual_price || (price * 12 * (1 - annualDiscount / 100));
  
  const creditPackages = settings?.credit_packages || [
    {"credits": 100, "price": 10.00, "label": "100 Credits"},
    {"credits": 1300, "price": 100.00, "label": "1,300 Credits"},
    {"credits": 3000, "price": 250.00, "label": "3,000 Credits"}
  ];
  const costPerQuery = settings?.credit_cost_per_query || 1;

  // Company contact info
  const companyName = settings?.company_name || "AIQueryStudio Inc.";
  const companyWebsite = settings?.company_website || "https://aiquerystudio.com";
  const companyEmail = settings?.company_email || "support@aiquerystudio.com";
  
  // CMS-managed content fields
  const heroBadge = getContent('hero-badge', "12 Database Types • No Long-Term Commitment");
  const heroTitle = getContent('hero-title', settings?.hero_title || "Manage All Your Databases from One Powerful Platform");
  const heroSubtitle = getContent('hero-subtitle', settings?.hero_subtitle || `Connect to MS SQL Server, MySQL, PostgreSQL, MongoDB, and 8+ other databases. Run queries, explore schemas, save favorite queries, and leverage AI-powered assistance—all in one intuitive web interface.`);
  const heroCtaPrimary = getContent('hero-cta-primary', "Start Free Trial");
  const heroCtaSecondary = getContent('hero-cta-secondary', "Sign In");
  const heroFootnote = getContent('hero-footnote', "No credit card required • Cancel anytime");
  const featuresTitle = getContent('features-title', settings?.features_title || "Powerful Features for Every Database");
  const featuresSubtitle = getContent('features-subtitle', settings?.features_subtitle || "Everything you need to manage SQL and NoSQL databases efficiently");
  const pricingTitle = getContent('pricing-title', settings?.pricing_title || "Simple, Credit-Based Pricing");
  const pricingSubtitle = getContent('pricing-subtitle', settings?.pricing_subtitle || "Start with full access to all features");
  const supportTitle = getContent('support-title', "Support & FAQ");
  const supportSubtitle = getContent('support-subtitle', "Get answers to common questions or chat with our AI assistant");
  const supportChatTitle = getContent('support-chat-title', "Need More Help?");
  const supportChatSubtitle = getContent('support-chat-subtitle', "Chat with our AI assistant for instant answers to your questions");
  const ctaTitle = getContent('cta-title', settings?.cta_title || "Ready to Get Started?");
  const ctaSubtitle = getContent('cta-subtitle', settings?.cta_subtitle || `Join thousands of developers and DBAs who trust ${siteName} for their database management needs.`);
  const ctaPrimaryBtn = getContent('cta-primary-btn', "Create Free Account");
  const ctaSecondaryBtn = getContent('cta-secondary-btn', "Ask a Question");
  const ctaFootnote = getContent('cta-footnote', "No credit card required • No long-term commitment • Cancel anytime");

  return (
    <div className="min-h-screen bg-[#09090b] text-foreground">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#09090b]/80 backdrop-blur-xl border-b border-border" data-testid="main-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button 
              onClick={() => navigate('/')} 
              className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity" 
              data-testid="nav-logo"
            >
              {logoUrl ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${logoUrl}`} alt={siteName} className="w-7 h-7 object-contain" data-testid="nav-logo-image" />
              ) : (
                <Database className="w-7 h-7" style={{ color: primaryColor }} data-testid="nav-logo-icon" />
              )}
              <span className="font-bold text-xl tracking-tight" data-testid="nav-site-name">{siteName}</span>
            </button>

            <div className="hidden md:flex items-center gap-6">
              <a href="#databases" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-link-databases">Databases</a>
              <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-link-features">Features</a>
              <a href="#pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-link-pricing">Pricing</a>
              <button onClick={() => navigate('/about')} className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-link-about">About</button>
              <a href="#support" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-link-support">Support</a>
              <Button variant="ghost" onClick={() => navigate('/login')} data-testid="nav-login-btn">
                Sign In
              </Button>
              <Button onClick={() => navigate('/register')} style={{ backgroundColor: primaryColor }} data-testid="nav-signup-btn">
                Get Started
              </Button>
            </div>

            <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-card" data-testid="mobile-menu">
            <div className="px-4 py-4 space-y-3">
              <a href="#databases" className="block text-sm text-muted-foreground hover:text-foreground" data-testid="mobile-nav-databases">Databases</a>
              <a href="#features" className="block text-sm text-muted-foreground hover:text-foreground" data-testid="mobile-nav-features">Features</a>
              <a href="#pricing" className="block text-sm text-muted-foreground hover:text-foreground" data-testid="mobile-nav-pricing">Pricing</a>
              <button onClick={() => navigate('/about')} className="block text-sm text-muted-foreground hover:text-foreground w-full text-left" data-testid="mobile-nav-about">About</button>
              <a href="#support" className="block text-sm text-muted-foreground hover:text-foreground" data-testid="mobile-nav-support">Support</a>
              <Button variant="outline" className="w-full" onClick={() => navigate('/login')} data-testid="mobile-login-btn">Sign In</Button>
              <Button className="w-full" onClick={() => navigate('/register')} style={{ backgroundColor: primaryColor }} data-testid="mobile-signup-btn">Get Started</Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4" data-testid="hero-section">
        <div className="max-w-7xl mx-auto text-center">
          <div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border mb-8"
            style={{ backgroundColor: `${primaryColor}10` }}
            data-testid="hero-badge"
          >
            <Zap className="w-4 h-4" style={{ color: primaryColor }} />
            <span className="text-sm">{renderHTML(heroBadge) || heroBadge}</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6" data-testid="hero-title">
            {renderHTML(heroTitle) || (
              <>
                {heroTitle.split(' ').slice(0, -2).join(' ')}
                <br />
                <span style={{ color: primaryColor }} data-testid="hero-title-accent">{heroTitle.split(' ').slice(-2).join(' ')}</span>
              </>
            )}
          </h1>

          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8" data-testid="hero-subtitle">
            {renderHTML(heroSubtitle) || heroSubtitle}
          </p>

          {/* Database badges */}
          <div className="flex flex-wrap justify-center gap-2 max-w-3xl mx-auto mb-10" data-testid="hero-database-badges">
            {databaseTypes.map((db, idx) => (
              <Badge 
                key={idx} 
                variant="secondary" 
                className="px-3 py-1 text-xs"
                data-testid={`hero-database-badge-${idx}`}
              >
                <span className="mr-1">{db.icon}</span>
                {db.name}
              </Badge>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="gap-2 px-8" 
              onClick={() => navigate('/register')}
              style={{ backgroundColor: primaryColor }}
              data-testid="hero-cta-btn"
            >
              {renderHTML(heroCtaPrimary) || heroCtaPrimary}
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="gap-2 px-8"
              onClick={() => navigate('/login')}
              data-testid="hero-signin-btn"
            >
              {renderHTML(heroCtaSecondary) || heroCtaSecondary}
            </Button>
          </div>

          <p className="text-sm text-muted-foreground mt-4" data-testid="hero-footnote">
            <Clock className="w-4 h-4 inline mr-1" />
            {renderHTML(heroFootnote) || heroFootnote}
          </p>

          {/* Hero Image */}
          <div className="mt-16 relative" data-testid="hero-preview">
            <div 
              className="absolute inset-0 rounded-xl opacity-20 blur-3xl"
              style={{ background: `linear-gradient(135deg, ${primaryColor}, transparent)` }}
              data-testid="hero-preview-glow"
            />
            <div className="relative bg-card border border-border rounded-xl overflow-hidden shadow-2xl" data-testid="hero-preview-frame">
              <div className="h-8 bg-muted/50 border-b border-border flex items-center gap-2 px-4" data-testid="hero-preview-toolbar">
                <div className="w-3 h-3 rounded-full bg-red-500/50" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/50" />
                <div className="w-3 h-3 rounded-full bg-green-500/50" />
                <span className="text-xs text-muted-foreground ml-2" data-testid="hero-preview-title">{siteName} - Database Management</span>
              </div>
              <div className="p-6 bg-[#0a0a0c]" data-testid="hero-preview-body">
                <div className="flex gap-4">
                  <div className="w-48 bg-card/50 rounded-lg p-4 hidden sm:block" data-testid="hero-preview-sidebar">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2" data-testid="hero-preview-connection-sql">
                        <Server className="w-4 h-4 text-blue-500" />
                        <span className="text-sm">SQL Server</span>
                      </div>
                      <div className="flex items-center gap-2" data-testid="hero-preview-connection-mongo">
                        <Database className="w-4 h-4 text-green-500" />
                        <span className="text-sm">MongoDB</span>
                      </div>
                      <div className="flex items-center gap-2" data-testid="hero-preview-connection-postgres">
                        <Database className="w-4 h-4 text-sky-500" />
                        <span className="text-sm">PostgreSQL</span>
                      </div>
                      <div className="flex items-center gap-2" data-testid="hero-preview-connection-mysql">
                        <Database className="w-4 h-4 text-orange-500" />
                        <span className="text-sm">MySQL</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 space-y-4" data-testid="hero-preview-main">
                    <div className="bg-muted/30 rounded-lg p-4 font-mono text-sm text-muted-foreground" data-testid="hero-preview-query">
                      <span className="text-blue-400">SELECT</span> * <span className="text-blue-400">FROM</span> Users
                      <br /><span className="text-blue-400">WHERE</span> status = <span className="text-green-400">'active'</span>
                      <br /><span className="text-blue-400">ORDER BY</span> created_at <span className="text-blue-400">DESC</span>
                    </div>
                    <div className="bg-muted/30 rounded-lg p-4 text-xs" data-testid="hero-preview-status">
                      <div className="flex items-center gap-2 text-green-500 mb-2">
                        <Check className="w-4 h-4" />
                        <span>Query executed successfully • 1,234 rows • 45ms</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Supported Databases Section */}
      <section id="databases" className="py-20 px-4 border-t border-border" data-testid="databases-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" data-testid="databases-title">
              Connect to Any Database
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto" data-testid="databases-subtitle">
              {siteName} supports 12 popular database systems, from traditional SQL to modern NoSQL
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="databases-grid">
            {databaseTypes.map((db, idx) => (
              <div 
                key={idx}
                className="p-4 rounded-xl border border-border bg-card/50 hover:bg-card hover:border-border/80 transition-all text-center group"
                data-testid={`database-card-${idx}`}
              >
                <div className="text-3xl mb-2 group-hover:scale-110 transition-transform" data-testid={`database-icon-${idx}`}>
                  {db.icon}
                </div>
                <h3 className="font-medium text-sm" data-testid={`database-name-${idx}`}>
                  {db.name}
                </h3>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center" data-testid="databases-footnote">
            <p className="text-muted-foreground text-sm">
              SQL & NoSQL • Cloud & On-Premise • Managed & Self-Hosted
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 border-t border-border" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16" data-testid="features-header">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" data-testid="features-title">
              {renderHTML(featuresTitle) || featuresTitle}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto" data-testid="features-subtitle">
              {renderHTML(featuresSubtitle) || featuresSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="features-grid">
            {features.map((feature, idx) => (
              <div 
                key={idx}
                className="p-6 rounded-xl border border-border bg-card/50 hover:bg-card transition-colors"
                data-testid={`feature-card-${idx}`}
              >
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${primaryColor}15` }}
                  data-testid={`feature-icon-${idx}`}
                >
                  <feature.icon className="w-6 h-6" style={{ color: primaryColor }} />
                </div>
                <h3 className="font-semibold text-base mb-2" data-testid={`feature-title-${idx}`}>{feature.title}</h3>
                <p className="text-muted-foreground text-sm" data-testid={`feature-desc-${idx}`}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* No Commitment Banner */}
      <section className="py-12 px-4 border-t border-border" style={{ backgroundColor: `${primaryColor}08` }} data-testid="commitment-banner">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6" data-testid="commitment-banner-content">
            <div className="flex items-center gap-3" data-testid="commitment-banner-item-commitment">
              <Clock className="w-8 h-8" style={{ color: primaryColor }} />
              <div className="text-left">
                <h3 className="font-semibold" data-testid="commitment-banner-title">No Long-Term Commitment</h3>
                <p className="text-sm text-muted-foreground" data-testid="commitment-banner-subtitle">Cancel your subscription anytime</p>
              </div>
            </div>
            <div className="hidden md:block w-px h-12 bg-border" data-testid="commitment-banner-divider" />
            <div className="flex items-center gap-3" data-testid="commitment-banner-item-access">
              <Zap className="w-8 h-8" style={{ color: primaryColor }} />
              <div className="text-left">
                <h3 className="font-semibold" data-testid="commitment-banner-access-title">Instant Access</h3>
                <p className="text-sm text-muted-foreground" data-testid="commitment-banner-access-subtitle">Start using in under 2 minutes</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <Pricing
        title={pricingTitle}
        subtitle={pricingSubtitle}
        primaryColor={primaryColor}
        pricingFeatures={pricingFeatures}
        fallbackPackages={creditPackages}
        fallbackCostPerQuery={costPerQuery}
        onCtaClick={() => navigate('/register')}
      />

      {/* Support Section */}
      <section id="support" className="py-20 px-4 border-t border-border" data-testid="support-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16" data-testid="support-header">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" data-testid="support-title">
              <HelpCircle className="w-8 h-8 inline-block mr-2" style={{ color: primaryColor }} />
              {renderHTML(supportTitle) || supportTitle}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto" data-testid="support-subtitle">
              {renderHTML(supportSubtitle) || supportSubtitle}
            </p>
          </div>

          {/* FAQ */}
          <div className="mb-12" data-testid="faq-section">
            <h3 className="text-xl font-semibold mb-6 flex items-center gap-2" data-testid="faq-title">
              <MessageCircle className="w-5 h-5" style={{ color: primaryColor }} />
              Frequently Asked Questions
            </h3>
            <Accordion type="single" collapsible className="space-y-2" data-testid="faq-accordion">
              {faqItems.map((item, idx) => (
                <AccordionItem 
                  key={idx} 
                  value={`item-${idx}`}
                  className="border border-border rounded-lg px-4 bg-card/50"
                  data-testid={`faq-item-${idx}`}
                >
                  <AccordionTrigger className="text-left hover:no-underline py-4" data-testid={`faq-question-${idx}`}>
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-4" data-testid={`faq-answer-${idx}`}>
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Chat CTA */}
          <div className="text-center p-8 rounded-2xl border border-border bg-card/50" data-testid="support-chat-cta">
            <Bot className="w-12 h-12 mx-auto mb-4" style={{ color: primaryColor }} data-testid="support-chat-icon" />
            <h3 className="text-xl font-semibold mb-2" data-testid="support-chat-title">{renderHTML(supportChatTitle) || supportChatTitle}</h3>
            <p className="text-muted-foreground mb-6" data-testid="support-chat-subtitle">
              {renderHTML(supportChatSubtitle) || supportChatSubtitle}
            </p>
            <Button 
              onClick={() => setChatOpen(true)}
              className="gap-2"
              style={{ backgroundColor: primaryColor }}
              data-testid="support-chat-btn"
            >
              <MessageCircle className="w-4 h-4" />
              Chat with QueryBot
            </Button>
          </div>

          {/* Contact Us */}
          <div className="mt-12 p-8 rounded-2xl border border-border bg-card/50" data-testid="support-contact">
            <h3 className="text-xl font-semibold mb-6 flex items-center gap-2" data-testid="support-contact-title">
              <Mail className="w-5 h-5" style={{ color: primaryColor }} />
              Contact Us
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="support-contact-grid">
              <div className="flex items-start gap-3" data-testid="support-contact-company">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Building2 className="w-5 h-5" style={{ color: primaryColor }} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Company</p>
                  <p className="font-medium" data-testid="support-company-name">{companyName}</p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-testid="support-contact-website">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Globe className="w-5 h-5" style={{ color: primaryColor }} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Website</p>
                  <a 
                    href={companyWebsite} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="font-medium hover:underline"
                    style={{ color: primaryColor }}
                    data-testid="support-company-website"
                  >
                    {companyWebsite.replace('https://', '').replace('http://', '')}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3" data-testid="support-contact-email">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Mail className="w-5 h-5" style={{ color: primaryColor }} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <a 
                    href={`mailto:${companyEmail}`}
                    className="font-medium hover:underline"
                    style={{ color: primaryColor }}
                    data-testid="support-company-email"
                  >
                    {companyEmail}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 border-t border-border" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" data-testid="cta-title">
            {renderHTML(ctaTitle) || ctaTitle}
          </h2>
          <p className="text-lg text-muted-foreground mb-8" data-testid="cta-subtitle">
            {renderHTML(ctaSubtitle) || ctaSubtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-testid="cta-actions">
            <Button 
              size="lg" 
              className="gap-2 px-8"
              onClick={() => navigate('/register')}
              style={{ backgroundColor: primaryColor }}
              data-testid="cta-register-btn"
            >
              {renderHTML(ctaPrimaryBtn) || ctaPrimaryBtn}
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="gap-2 px-8"
              onClick={() => setChatOpen(true)}
              data-testid="cta-chat-btn"
            >
              <MessageCircle className="w-4 h-4" />
              {renderHTML(ctaSecondaryBtn) || ctaSecondaryBtn}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-6" data-testid="cta-footnote">
            {renderHTML(ctaFootnote) || ctaFootnote}
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-border" data-testid="footer">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4" data-testid="footer-content">
          <div className="flex items-center gap-3" data-testid="footer-logo">
            {logoUrl ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${logoUrl}`} alt={siteName} className="w-5 h-5 object-contain" data-testid="footer-logo-image" />
            ) : (
              <Database className="w-5 h-5" style={{ color: primaryColor }} data-testid="footer-logo-icon" />
            )}
            <span className="font-semibold" data-testid="footer-site-name">{siteName}</span>
            <span className="text-xs text-muted-foreground border-l border-border pl-3 font-mono tabular-nums" data-testid="footer-datetime">
              {footerTime.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}
              {' · '}
              {footerTime.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          </div>
          <div className="flex items-center gap-6" data-testid="footer-links">
            <button onClick={() => navigate('/about')} className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-about-link">
              About
            </button>
            <button onClick={() => navigate('/privacy')} className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-privacy-link">
              Privacy
            </button>
            <a href="#support" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-faq-link">
              FAQ
            </a>
            <button 
              onClick={() => setChatOpen(true)}
              className="text-sm text-muted-foreground hover:text-foreground"
              data-testid="footer-support-chat"
            >
              Support Chat
            </button>
            <a 
              href="/admin"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-muted-foreground hover:text-foreground"
              data-testid="footer-admin-link"
            >
              Admin Login
            </a>
            <p className="text-sm text-muted-foreground" data-testid="footer-copyright">
              © {new Date().getFullYear()} {siteName}. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating Chat Button */}
      {!chatOpen && (
        <button
          onClick={() => setChatOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg flex items-center justify-center z-40 hover:scale-105 transition-transform"
          style={{ backgroundColor: primaryColor }}
          data-testid="chat-fab"
        >
          <MessageCircle className="w-6 h-6 text-white" />
        </button>
      )}

      {/* Chatbot */}
      <ChatBot isOpen={chatOpen} onClose={() => setChatOpen(false)} primaryColor={primaryColor} />
    </div>
  );
};

export default LandingPage;
